import React, { useState } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { Alert, Button, Link, Text, TextField } from "@merge-api/merge-javascript-shared";

import { fetchWithoutAuth, FormErrorData, hasAuthToken } from "../../../../api-client/APIClient";
import { showErrorToast } from "../../../shared/Toasts";
import useTimeout from "../../../shared/hooks/useTimeout";
import { LOGIN_PATH, navigateToLoginPage, useQuery } from "../../../../router/RouterUtils";
import { SEC_PASSWORD_RESET_AUTO_REDIRECT } from "../../../../constants";

import AuthCard from "../shared/AuthCard";
import AuthPageWrapper from "../shared/AuthPageWrapper";

const ResetPasswordScreen = () => {
  const query = useQuery();
  const email = query.get("email");
  const token = query.get("token");
  const history = useHistory();

  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | undefined>();
  const [errorBanner, setErrorBanner] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resetComplete, setResetComplete] = useState<boolean>(false);

  useTimeout(
    () => navigateToLoginPage(history),
    resetComplete ? SEC_PASSWORD_RESET_AUTO_REDIRECT : null,
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 8 || newPassword.length > 100) {
      setPasswordError("Please enter a valid password at least 8 characters in length.");
      return;
    }
    setPasswordError(undefined);
    setIsLoading(true);

    fetchWithoutAuth({
      path: "/users/password/reset",
      method: "POST",
      body: { email, password: newPassword, email_token: token },
      onResponse: () => {
        setIsLoading(false);
        setResetComplete(true);
      },
      onError: (err) => {
        if (err) {
          err.json().then((data: FormErrorData) => {
            for (const field in data) {
              if (field === "non_field_errors") {
                setErrorBanner(data[field][0]);
                continue;
              }
              if (field === "new_password" || field === "password") {
                setPasswordError(data[field][0]);
              }
            }
          });
        } else {
          // Network failure (no response body to thread into the form) — toast it.
          // Structured backend errors render inline; we don't want to double-notify.
          showErrorToast("Failed to reset password. Please try again.");
        }
        setIsLoading(false);
      },
    });
  };

  if (hasAuthToken()) return <Redirect to="/" />;

  return (
    <AuthPageWrapper>
      <AuthCard className="p-8 flex flex-col gap-6">
        <Text variant="h2">Reset password</Text>

        {resetComplete ? (
          <div className="flex flex-col items-center gap-4 text-center">
            <Text>Your password was successfully reset.</Text>
            <Text variant="sm" className="text-gray-40">
              Redirecting to login screen in {SEC_PASSWORD_RESET_AUTO_REDIRECT}s…
            </Text>
            <Link href={LOGIN_PATH} variant="sm">
              Skip to login screen
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
            {errorBanner && <Alert color="red">{errorBanner}</Alert>}
            <TextField
              name="new_password"
              type="password"
              labelTitle="New password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (passwordError) setPasswordError(undefined);
              }}
              error={!!passwordError}
              errorText={passwordError}
            />
            <Button type="submit" fullWidth loading={isLoading}>
              Submit
            </Button>
            <div className="text-center">
              <Link href={LOGIN_PATH} variant="sm">
                Back to login
              </Link>
            </div>
          </form>
        )}
      </AuthCard>
    </AuthPageWrapper>
  );
};

export default ResetPasswordScreen;
